import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Phone, Mail, MapPin, ArrowRight } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-matte-900 text-cream-300" itemScope itemType="https://schema.org/ProfessionalService">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="mb-6">
              <span className="font-serif text-2xl text-white tracking-widest block" itemProp="name">
                ANANDA PAUDEL
              </span>
              <span className="font-sans text-[10px] tracking-[0.3em] text-walnut-300 uppercase">
                CONSULT · DESIGN · BUILD · NEPAL
              </span>
            </div>
            <p className="font-sans text-sm text-matte-300 leading-relaxed mb-6" itemProp="description">
              Bespoke interior design and custom furniture for Nepali homes.
              40+ years of factory-direct experience — from the first
              consultation to the day your pieces are installed.
            </p>
            <div className="flex gap-4">
              <a
                href="#"
                aria-label="Instagram"
                className="w-9 h-9 border border-matte-600 hover:border-walnut-400 rounded-full flex items-center justify-center text-matte-400 hover:text-walnut-300 transition-all duration-300"
              >
                <Instagram size={15} />
              </a>
              <a
                href="#"
                aria-label="Facebook"
                className="w-9 h-9 border border-matte-600 hover:border-walnut-400 rounded-full flex items-center justify-center text-matte-400 hover:text-walnut-300 transition-all duration-300"
              >
                <Facebook size={15} />
              </a>
              <a
                href="#"
                aria-label="YouTube"
                className="w-9 h-9 border border-matte-600 hover:border-walnut-400 rounded-full flex items-center justify-center text-matte-400 hover:text-walnut-300 transition-all duration-300"
              >
                <Youtube size={15} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-white mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Overview", to: "/services" },
                { label: "01 · Consulting", to: "/services/consulting" },
                { label: "02 · Design", to: "/services/design" },
                { label: "03 · Build & Deliver", to: "/services/build" },
                { label: "Book a Consultation", to: "/book" },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors flex items-center gap-1 group"
                  >
                    <ArrowRight
                      size={12}
                      className="opacity-0 group-hover:opacity-100 transition-opacity -ml-3 group-hover:ml-0"
                    />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-white mb-5">
              Portfolio
            </h4>
            <ul className="space-y-3">
              {[
                { label: "All Designs", to: "/portfolio" },
                { label: "Sofas", to: "/portfolio?category=sofas" },
                { label: "Wardrobes", to: "/portfolio?category=wardrobes" },
                { label: "Beds", to: "/portfolio?category=beds" },
                { label: "Dressing Tables", to: "/portfolio?category=dressing-tables" },
                { label: "Kids Furniture", to: "/portfolio?category=kids-furniture" },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-white mt-8 mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors">
                  About Ananda
                </Link>
              </li>
              <li>
                <Link to="/contact" className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-white mb-5">
              Get in Touch
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-3" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <MapPin size={16} className="text-walnut-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-xs text-matte-500 mb-0.5 uppercase tracking-wider">
                    Based In
                  </p>
                  <p className="font-sans text-sm text-matte-400">
                    <span itemProp="addressLocality">Kathmandu</span>,{" "}
                    <span itemProp="addressRegion">Bagmati</span> ·{" "}
                    <span itemProp="addressCountry">Nepal</span>
                  </p>
                  <p className="font-sans text-xs text-matte-500 mt-0.5">
                    Serving Kathmandu Valley & Nepal-wide
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-walnut-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-xs text-matte-500 mb-0.5 uppercase tracking-wider">
                    Call or WhatsApp
                  </p>
                  <a
                    href="tel:+9779841488271"
                    itemProp="telephone"
                    className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors"
                  >
                    +977-9841488271
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-walnut-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-xs text-matte-500 mb-0.5 uppercase tracking-wider">
                    Email
                  </p>
                  <a
                    href="mailto:ananda.paudel.work@gmail.com"
                    itemProp="email"
                    className="font-sans text-sm text-matte-400 hover:text-cream-300 transition-colors break-all"
                  >
                    ananda.paudel.work@gmail.com
                  </a>
                </div>
              </li>
            </ul>
            <div className="mt-6 p-4 border border-walnut-700 rounded-sm bg-walnut-900/20">
              <p className="font-sans text-xs text-walnut-400 mb-1 uppercase tracking-wider">
                Hours
              </p>
              <p className="font-sans text-sm text-cream-400">
                Sun – Fri · 9:00 – 19:00
              </p>
              <p className="font-sans text-xs text-matte-500 mt-1">
                Typical reply within a few hours.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-matte-800 max-w-7xl mx-auto px-4 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-sans text-xs text-matte-500 text-center">
          © {new Date().getFullYear()} Ananda Paudel — Interior Designer &amp; Furniture Specialist, Nepal. All rights reserved.
        </p>
        <div className="flex gap-6">
          <Link to="/about" className="font-sans text-xs text-matte-500 hover:text-matte-300 transition-colors">
            About
          </Link>
          <Link to="/services" className="font-sans text-xs text-matte-500 hover:text-matte-300 transition-colors">
            Services
          </Link>
          <Link to="/contact" className="font-sans text-xs text-matte-500 hover:text-matte-300 transition-colors">
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}
